import { useFpB2bPaymentService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-service'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiGetFpPayment } from '@hooks-api/fortune/useApiGetFpPayment'
import { PaymentMethodMap, PaymentMethodType, PaymentServiceType } from '@hooks-api/types/dataModel/payment'
import { isApplePaySupported } from '@payments'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Radio } from '@pets-ui/Radio/Radio'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom-v5'
import { useContextPayment } from '../../../../fp-b2b-shared/components/PaymentProvider/PaymentProvider'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'
import { PaymentAliPay } from './PaymentAliPay'
import { PaymentCheckoutPayApplePay } from './PaymentCheckoutPayApplePay'
import { PaymentCreditCard } from './PaymentCreditCard'
import { PaymentCvs } from './PaymentCvs'
import { PaymentEcpayAtm } from './PaymentEcpayAtm'
import { PaymentLinePay } from './PaymentLinePay'
import { PaymentPayMe } from './PaymentPayMe'
import { PaymentTappayApplePay } from './PaymentTappayApplePay'
import { PaymentTappayJkoPay } from './PaymentTappayJkoPay'

export const paymentComponentStyle = stylex.create({
  root: {
    padding: 16,
    borderColor: colors.grey300,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    gap: 8,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
  },
  radioPaymentChecked: {
    backgroundColor: colors.white,
  },
  fullWidth: {
    width: '100%',
  },
  codBox: {
    flex: '1',
  },
})

export const PaymentMethodComponent = () => {
  const { selectedPayment, setSelectedPayment, paymentMethods, setPaymentExecution, setConfiguredPaymentModule, setIsLoadingPayment } = useContextPayment()
  const { campaignId, campaignCode, platform } = useCheckout()
  const { trigger: getPaymentToken } = useApiGetFpPayment()
  const typesAndServices = paymentMethods.map(method => ({ type: method.type, service: method.service_type }))
  const [creditCardKey, setCreditCardKey] = useState<number | null>(null)

  const { transactionId: paymentTransactionIdFromUrl } = useParams<{ transactionId?: string }>()
  const { orderId, orderType } = useFpB2bPaymentService({ campaignId, paymentTransactionIdFromUrl, campaignCode, platform })
  const handleSelectPayment = useCallbackEvent(async (type: PaymentMethodType) => {
    const currentMethod = paymentMethods.find(method => method.type === type)
    if (currentMethod?.type === PaymentMethodType.CREDIT_CARD_ONCE) {
      setCreditCardKey(currentMethod.id)
    }
    try {
      if (!campaignCode || !currentMethod) {
        throw new Error('Missing campaign code, current method')
      }
      setSelectedPayment(currentMethod)

      setIsLoadingPayment(true)
      const token = await getPaymentToken({
        order_type: orderType,
        order_id: orderId,
        payment_method_id: currentMethod.id,
      })

      const { payment_transaction_id } = token.data.data
      setPaymentExecution({
        payment_transaction_id,
        service_pay_info: null,
        user_payment_token_info: undefined,
        payment_success_url: '',
        payment_fallback_url: '',
      })
      setConfiguredPaymentModule(undefined)
    }
    catch (error) {
      console.error(`[PaymentCheckout] ${type}, ${error}`)
    }
    finally {
      setIsLoadingPayment(false)
    }
  })
  const handleInitPayment = useCallback(async () => {
    if (!selectedPayment?.type) return
    if (![PaymentMethodType.ASSIGNED_PAYMENT_INVOICE, PaymentMethodType.CASH_ON_DELIVERY, PaymentMethodType.ASSIGNED_PAYMENT].includes(selectedPayment.type)) return
    handleSelectPayment(selectedPayment?.type)
  }, [handleSelectPayment, selectedPayment?.type])
  // NOTE: for default selection
  useEffect(() => {
    handleInitPayment()
  }, [handleInitPayment])

  useEffect(() => {
    function handleDefaultPaymentWhenSelectedPaymentIsNotInPaymentMethods() {
      if (!paymentMethods.find(method => method.id === selectedPayment?.id)) {
        if (!paymentMethods[0]?.type) return
        setConfiguredPaymentModule(undefined)
        handleSelectPayment(paymentMethods[0]?.type)
      }
    }
    handleDefaultPaymentWhenSelectedPaymentIsNotInPaymentMethods()
  }, [handleSelectPayment, paymentMethods, selectedPayment?.id, setConfiguredPaymentModule])

  return (
    <VStack gap={3} rootStyle={paymentComponentStyle.fullWidth}>
      {typesAndServices.find(object => (object.type === PaymentMethodType.CREDIT_CARD_ONCE)) && <PaymentCreditCard key={creditCardKey} />}
      {typesAndServices.find(object => (object.service === PaymentServiceType.TAPPAY && object.type === PaymentMethodType.APPLE_PAY)) && isApplePaySupported() && <PaymentTappayApplePay />}
      {typesAndServices.find(object => (object.service === PaymentServiceType.CHECKOUTPAY && object.type === PaymentMethodType.APPLE_PAY)) && <PaymentCheckoutPayApplePay />}
      {typesAndServices.find(object => (object.service === PaymentServiceType.LINEPAY && object.type === PaymentMethodType.LINE_PAY)) && <PaymentLinePay />}
      {typesAndServices.find(object => (object.service === PaymentServiceType.PAYME && object.type === PaymentMethodType.PAYME)) && <PaymentPayMe />}
      {typesAndServices.find(object => (object.service === PaymentServiceType.ECPAY && object.type === PaymentMethodType.ATM)) && <PaymentEcpayAtm />}
      {typesAndServices.find(object => (object.service === PaymentServiceType.TAPPAY && object.type === PaymentMethodType.JKO_PAY)) && <PaymentTappayJkoPay />}
      {typesAndServices.find(object => (object.service === PaymentServiceType.ECPAY && object.type === PaymentMethodType.CVS)) && <PaymentCvs />}
      {typesAndServices.find(object => (object.service === PaymentServiceType.CHECKOUTPAY && object.type === PaymentMethodType.ALIPAY_HK)) && <PaymentAliPay />}
      {typesAndServices.find(object => (object.type === PaymentMethodType.CASH_ON_DELIVERY))
      && (
        <Box component="label" rootStyle={[paymentComponentStyle.root, paymentComponentStyle.radioPaymentChecked]}>
          <VStack flexGrow={1} gap={2.5}>
            <HStack alignItems="center" gap={2}>
              <Radio
                name="paymentCod"
                checked={selectedPayment?.type === PaymentMethodType.CASH_ON_DELIVERY}
                value="paymentCod"
                onChange={() => handleSelectPayment(PaymentMethodType.CASH_ON_DELIVERY)}
              />
              <Text fontWeight={600} lineHeight="100%">{PaymentMethodMap[PaymentMethodType.CASH_ON_DELIVERY]}</Text>
              <Box flexGrow={1} />
            </HStack>
          </VStack>
          <Text variant="body" color={colors.grey500}>收貨時以現金付款給黑貓送貨大哥就可以囉 ! 請確認電話、地址與到貨時段是否填寫正確</Text>
        </Box>
      )}

      {typesAndServices.find(object => (object.type === PaymentMethodType.ASSIGNED_PAYMENT))
      && (
        <Box component="label" rootStyle={[paymentComponentStyle.root, paymentComponentStyle.radioPaymentChecked]}>
          <VStack flexGrow={1} gap={2.5}>
            <HStack alignItems="center" gap={2}>
              <Radio
                name="assignedPayment"
                checked={selectedPayment?.type === PaymentMethodType.ASSIGNED_PAYMENT}
                value="assignedPayment"
                onChange={() => handleSelectPayment(PaymentMethodType.ASSIGNED_PAYMENT)}
              />
              <Text fontWeight={600} lineHeight="100%">
                {PaymentMethodMap[PaymentMethodType.ASSIGNED_PAYMENT]}
              </Text>
            </HStack>
          </VStack>
        </Box>
      )}

      {typesAndServices.find(object => (object.type === PaymentMethodType.ASSIGNED_PAYMENT_INVOICE))
      && (
        <Box component="label" rootStyle={[paymentComponentStyle.root, paymentComponentStyle.radioPaymentChecked]}>
          <VStack flexGrow={1} gap={2.5}>
            <HStack alignItems="center" gap={2}>
              <Radio
                name="assignedPaymentInvoice"
                checked={selectedPayment?.type === PaymentMethodType.ASSIGNED_PAYMENT_INVOICE}
                value="assignedPaymentInvoice"
                onChange={() => handleSelectPayment(PaymentMethodType.ASSIGNED_PAYMENT_INVOICE)}
              />
              <Text fontWeight={600} lineHeight="100%">
                {PaymentMethodMap[PaymentMethodType.ASSIGNED_PAYMENT_INVOICE]}
              </Text>
            </HStack>
          </VStack>
        </Box>
      )}
    </VStack>
  )
}
